import { useState, useCallback } from "react";
import {
  BillDataEntity,
  CreateZohoBillUseCase,
  UpdateZohoBillUseCase,
  RejectBillUseCase,
  MarkBillAsDuplicateUseCase,
  DeletePostedBillUseCase,
  UpdateBillJsonUseCase,
} from "../../domain";
import { BillRepositoryImpl } from "../../data/repositories/BillRepositoryImpl";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";
import {
  getFailedCreateInBackendMessage,
  getFailedUpdateInBackendMessage,
  getStoredAccountingBackend,
} from "../../../../config/accounting";

// Create repository instance (singleton)
const billRepository = new BillRepositoryImpl();

// Create use cases
const createZohoBillUseCase = new CreateZohoBillUseCase(billRepository);
const updateZohoBillUseCase = new UpdateZohoBillUseCase(billRepository);
const rejectBillUseCase = new RejectBillUseCase(billRepository);
const markBillAsDuplicateUseCase = new MarkBillAsDuplicateUseCase(
  billRepository,
);
const deletePostedBillUseCase = new DeletePostedBillUseCase(billRepository);
const updateBillJsonUseCase = new UpdateBillJsonUseCase(billRepository);

export interface UseBillOperationsReturn {
  loading: boolean;
  error: string | null;

  createZohoBill: (
    billId: string,
    data: BillDataEntity,
    pdfUrl: string,
    approvalAudit?: ApprovalAuditPayload,
  ) => Promise<boolean>;
  updateZohoBill: (
    billId: string,
    data: BillDataEntity,
    approvalAudit?: ApprovalAuditPayload,
  ) => Promise<boolean>;
  updateBillJson: (billId: string, data: BillDataEntity) => Promise<boolean>;
  rejectBill: (billId: number) => Promise<boolean>;
  markAsDuplicate: (billId: number) => Promise<boolean>;
  deletePostedBill: (billId: number) => Promise<boolean>;

  clearError: () => void;
}

export const useBillOperations = (): UseBillOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accountingBackend = getStoredAccountingBackend();

  /**
   * Create bill in Zoho Books
   */
  const createZohoBill = useCallback(
    async (
      billId: string,
      data: BillDataEntity,
      pdfUrl: string,
      approvalAudit?: ApprovalAuditPayload,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createZohoBillUseCase.execute(
          billId,
          data,
          pdfUrl,
          approvalAudit,
        );

        if (!result.success) {
          throw new Error(result.error || getFailedCreateInBackendMessage("bill", accountingBackend));
        }

        return result.success;
      } catch (error: any) {
        const errorMessage = error.message || getFailedCreateInBackendMessage("bill", accountingBackend);
        setError(errorMessage);
        console.error("Failed to create Zoho bill:", error);
        // Re-throw the error so it can be caught by the component immediately
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountingBackend],
  );

  /**
   * Update bill in accounting backend
   */
  const updateZohoBill = useCallback(
    async (
      billId: string,
      data: BillDataEntity,
      approvalAudit?: ApprovalAuditPayload,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateZohoBillUseCase.execute(
          billId,
          data,
          approvalAudit,
        );

        if (!result.success) {
          throw new Error(result.error || getFailedUpdateInBackendMessage("bill", accountingBackend));
        }

        return result.success;
      } catch (error: any) {
        const errorMessage = error.message || getFailedUpdateInBackendMessage("bill", accountingBackend);
        setError(errorMessage);
        console.error("Failed to update Zoho bill:", error);
        // Re-throw the error so it can be caught by the component immediately
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountingBackend],
  );

  /**
   * Update bill JSON data (for auto-save and manual edits)
   */
  const updateBillJson = useCallback(
    async (billId: string, data: BillDataEntity): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateBillJsonUseCase.execute(billId, data);

        if (!result.success) {
          throw new Error(result.error || "Failed to update bill JSON");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update bill JSON";
        setError(errorMessage);
        console.error("Failed to update bill JSON:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Reject a bill
   */
  const rejectBill = useCallback(async (billId: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const result = await rejectBillUseCase.execute(billId);

      if (!result.success) {
        throw new Error(result.error || "Failed to reject bill");
      }

      return true;
    } catch (error: any) {
      const errorMessage = error.message || "Failed to reject bill";
      setError(errorMessage);
      console.error("Failed to reject bill:", error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mark bill as duplicate
   */
  const markAsDuplicate = useCallback(
    async (billId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await markBillAsDuplicateUseCase.execute(billId);

        if (!result.success) {
          throw new Error(result.error || "Failed to mark bill as duplicate");
        }

        return true;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to mark bill as duplicate";
        setError(errorMessage);
        console.error("Failed to mark bill as duplicate:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Delete posted bill (from Zoho and soft-delete locally)
   */
  const deletePostedBill = useCallback(
    async (billId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await deletePostedBillUseCase.execute(billId);

        if (!result.success) {
          throw new Error(result.error || "Failed to delete bill");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to delete bill";
        setError(errorMessage);
        console.error("Failed to delete bill:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createZohoBill,
    updateZohoBill,
    updateBillJson,
    rejectBill,
    markAsDuplicate,
    deletePostedBill,
    clearError,
  };
};
